import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { AuthError } from "./types";
import { loginRequired, showSessionTimeoutToast } from "./utils";
import { logger } from "../datadog";
import { useSetAtom } from "jotai";
import { needsReLoginAtom } from "./state";

export class NamedAuthError extends Error {
  constructor(name: string, message: string) {
    super(message);
    this.name = name;
  }
}

export class NamedLoginError extends Error {
  constructor(name: string, message: string) {
    super(message);
    this.name = name;
  }
}

/**
 * Hook that returns a wrapper function for `getAccessTokenSilently` from the
 * Auth0 SDK that handles errors that may occur when trying to get the access
 * token.
 *
 * If the user's session has expired and they need to re-login, the function can
 * optionally show a toast directing them to re-login. It will also set the
 * value for the `needsReLoginAtom` atom, which can be used to react to session
 * expiration from other parts of the app, e.g. to disable UI.
 *
 * @returns A function to get the access token silently. The function returns a
 * tuple containing the access token and an error if one occurred.
 */
export function useGetAccessTokenSilentlyCallback(): (options?: {
  showToast?: boolean;
}) => Promise<
  | [token: string, error: null]
  | [token: null, error: NamedAuthError | NamedLoginError]
> {
  const setNeedsReLoginAtom = useSetAtom(needsReLoginAtom);
  const { getAccessTokenSilently } = useAuth0();

  return useCallback(
    async ({ showToast = false } = {}): Promise<
      | [token: string, error: null]
      | [token: null, error: NamedAuthError | NamedLoginError]
    > => {
      try {
        const token = await getAccessTokenSilently();
        setNeedsReLoginAtom(false);
        return [token, null];
      } catch (_e) {
        const e = _e as AuthError;

        if (loginRequired(e.error)) {
          setNeedsReLoginAtom(true);
          if (showToast) showSessionTimeoutToast();
          const error = new NamedLoginError(e.error, e.error_description);
          logger.warn("Login required", {}, error);
          return [null, error];
        }

        const error = new NamedAuthError(e.error, e.error_description);
        logger.error("An unexpected auth error occurred", {}, error);
        return [null, error];
      }
    },
    [getAccessTokenSilently, setNeedsReLoginAtom],
  );
}
